import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { default as Select } from 'antd/es/select';
import { useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { convertFieldToHumanReadable } from 'utils/formatters/fieldToDisplayableName';
import { withSandbox } from 'utils/withSandbox';

import type { CollectionOrderPaymentItem, CollectionPaymentStatus } from 'api';

import {
  PutSandboxOrdersUuidTestCaseBodyTestCase,
  usePutSandboxOrdersUuidTestCase
} from 'api/sandboxApi';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';

import { OrderPaymentEntry } from '@molecules/OrderPaymentEntry';

import { sandboxT } from './translations';

export const SANDBOX_TEST_CASE_DROPDOWN_TEST_ID =
  'collection-order-details-sandbox-test-case-dropdown';

const validPaymentStatuses: ReadonlySet<CollectionPaymentStatus> = new Set([
  'redirected_to_inpay',
  'returned_from_provider',
  'redirect_to_bank',
  'bank_processing',
  'bank_authorised',
  'instructions_provided',
  'sum_too_low',
  'sum_too_high',
  'processing',
  'user_authorised'
]);

const options = Object.values(PutSandboxOrdersUuidTestCaseBodyTestCase).map(
  (testCase) => ({
    label: convertFieldToHumanReadable(testCase),
    value: testCase
  })
);

type Props = {
  orderUuid: string;
  payment: CollectionOrderPaymentItem;
  onSuccess: VoidFunction;
};

export const SandboxOrderPaymentEntry = withSandbox(
  ({ orderUuid, payment, onSuccess }: Props) => {
    const organisationId = useOrganisationId<number>();
    const openNotification = useOpenNotification();

    const [selectedTestCase, setSelectedTestCase] =
      useState<PutSandboxOrdersUuidTestCaseBodyTestCase | null>(null);

    const { mutate, isPending, isSuccess } = usePutSandboxOrdersUuidTestCase();

    const executeTestCase = () => {
      if (selectedTestCase) {
        mutate(
          {
            organisationId,
            uuid: orderUuid,
            data: {
              payment_uuid: payment.uuid,
              test_case: selectedTestCase
            }
          },
          {
            onSuccess: () => {
              openNotification({
                kind: 'success',
                message: sandboxT.common.notifications.testCaseExecuted
              });
              onSuccess();
            },
            onError: (err) => {
              // err type is misleading, in case of 5xx errors it's just a string
              openNotification({
                kind: 'error',
                message:
                  err.error?.title || sandboxT.common.notifications.serverError
              });
            }
          }
        );
      }
    };

    const disabled =
      isPending || isSuccess || !validPaymentStatuses.has(payment.status);

    return (
      <div
        className="border-b py-6 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-3"
        key={payment.unique_reference}
      >
        <OrderPaymentEntry payment={payment} className="border-none" />
        <div className="flex gap-2">
          <Select
            placeholder={sandboxT.common.placeholders.selectTestCase}
            className="grow"
            size="large"
            suffixIcon={<ChevronDownIcon width={20} height={20} />}
            options={options}
            value={selectedTestCase}
            onSelect={setSelectedTestCase}
            data-testid={SANDBOX_TEST_CASE_DROPDOWN_TEST_ID}
            disabled={disabled}
          />
          <PrimaryButton
            className="shrink-0"
            disabled={!selectedTestCase || disabled}
            onClick={executeTestCase}
          >
            {sandboxT.common.actions.execute}
          </PrimaryButton>
        </div>
      </div>
    );
  }
);
